import { FC } from "react";
import { MigrationObjectType } from "@common/types/domains/accounting/accountingMigration";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { CheckboxIndicator } from "@design-system/Checkbox/CheckboxIndicator";
import { joinWithAnd } from "@helpers/joinWithAnd";
import styles from "./HistoricalDataFields.module.scss";

interface Props {
    syncGeneralLedger: boolean;
    syncCounterparties: boolean;
    syncAttachments: boolean;
    syncClasses: boolean;
    syncCatalogItems: boolean;
    syncInvoices: boolean;
    syncBills: boolean;
    onToggleGeneralLedger: () => void;
    onToggleCounterparties: () => void;
    onToggleAttachments: () => void;
    onToggleClasses: () => void;
    onToggleCatalogItems: () => void;
    onToggleInvoices: () => void;
    onToggleBills: () => void;
    hideAttachments?: boolean;
    hideCounterparties?: boolean;
    lockedObjectTypes?: ReadonlySet<MigrationObjectType>;
    lockReasonByObjectType?: ReadonlyMap<MigrationObjectType, string>;
    cascadeIncludesGeneralLedger?: boolean;
    cascadeIncludesClasses?: boolean;
}

export const MIGRATION_OBJECT_TYPE_LABELS: Record<MigrationObjectType, string> =
    {
        [MigrationObjectType.GENERAL_LEDGER]: "General ledger",
        [MigrationObjectType.COUNTERPARTIES]: "Customers & vendors",
        [MigrationObjectType.ATTACHMENTS]: "Attachments",
        [MigrationObjectType.CLASSES]: "Classes",
        [MigrationObjectType.CATALOG_ITEMS]: "Products & services",
        [MigrationObjectType.INVOICES]: "Invoices",
        [MigrationObjectType.BILLS]: "Bills",
    };

const GENERAL_LEDGER_LABEL =
    MIGRATION_OBJECT_TYPE_LABELS[MigrationObjectType.GENERAL_LEDGER];
const COUNTERPARTIES_LABEL =
    MIGRATION_OBJECT_TYPE_LABELS[MigrationObjectType.COUNTERPARTIES];
const CLASSES_LABEL = MIGRATION_OBJECT_TYPE_LABELS[MigrationObjectType.CLASSES];

interface CascadeNoteParams {
    hideCounterparties: boolean;
    cascadeIncludesGeneralLedger: boolean;
    cascadeIncludesClasses: boolean;
}

function getSharedCascadeItems({
    hideCounterparties,
    cascadeIncludesGeneralLedger,
    cascadeIncludesClasses,
}: CascadeNoteParams): string[] {
    return [
        ...(cascadeIncludesGeneralLedger ? [GENERAL_LEDGER_LABEL] : []),
        ...(hideCounterparties ? [] : [COUNTERPARTIES_LABEL]),
        ...(cascadeIncludesClasses ? [CLASSES_LABEL] : []),
    ];
}

function getInvoicesCascadeNote(params: CascadeNoteParams): string {
    const items = [
        ...getSharedCascadeItems(params),
        MIGRATION_OBJECT_TYPE_LABELS[MigrationObjectType.CATALOG_ITEMS],
    ];
    return `Selecting Invoices also selects ${joinWithAnd(items)}.`;
}

function getBillsCascadeNote(params: CascadeNoteParams): string | undefined {
    const items = getSharedCascadeItems(params);
    return items.length > 0
        ? `Selecting Bills also selects ${joinWithAnd(items)}.`
        : undefined;
}

interface MigrationCheckboxItemProps {
    label: string;
    description: string;
    checked: boolean;
    disabled: boolean;
    lockReason: string | undefined;
    onToggle: () => void;
}

const MigrationCheckboxItem: FC<MigrationCheckboxItemProps> = ({
    label,
    description,
    checked,
    disabled,
    lockReason,
    onToggle,
}) => (
    <button
        type="button"
        className={styles.migrationCheckboxItem}
        role="checkbox"
        aria-checked={checked}
        disabled={disabled}
        onClick={onToggle}
    >
        <HStack as="span" gap={12} padding={16}>
            <CheckboxIndicator
                state={checked ? "checked" : "unchecked"}
                disabled={disabled}
            />
            <VStack as="span" gap={2}>
                <span className={styles.migrationCheckboxTitle}>{label}</span>
                <span className={styles.migrationCheckboxDescription}>
                    {description}
                    {lockReason ? ` ${lockReason}.` : ""}
                </span>
            </VStack>
        </HStack>
    </button>
);

export const FullMigrationOptions: FC<Props> = ({
    syncGeneralLedger,
    syncCounterparties,
    syncAttachments,
    syncClasses,
    syncCatalogItems,
    syncInvoices,
    syncBills,
    onToggleGeneralLedger,
    onToggleCounterparties,
    onToggleAttachments,
    onToggleClasses,
    onToggleCatalogItems,
    onToggleInvoices,
    onToggleBills,
    hideAttachments = false,
    hideCounterparties = false,
    lockedObjectTypes,
    lockReasonByObjectType,
    cascadeIncludesGeneralLedger = true,
    cascadeIncludesClasses = false,
}) => {
    const rows: Array<{
        objectType: MigrationObjectType;
        label: string;
        description: string;
        checked: boolean;
        onToggle: () => void;
        hidden?: boolean;
    }> = [
        {
            objectType: MigrationObjectType.GENERAL_LEDGER,
            label: MIGRATION_OBJECT_TYPE_LABELS[
                MigrationObjectType.GENERAL_LEDGER
            ],
            description: "All journal entries and account balances.",
            checked: syncGeneralLedger,
            onToggle: onToggleGeneralLedger,
        },
        {
            objectType: MigrationObjectType.COUNTERPARTIES,
            label: MIGRATION_OBJECT_TYPE_LABELS[
                MigrationObjectType.COUNTERPARTIES
            ],
            description: "Counterparty records used in AR / AP.",
            checked: syncCounterparties,
            onToggle: onToggleCounterparties,
            hidden: hideCounterparties,
        },
        {
            objectType: MigrationObjectType.ATTACHMENTS,
            label: MIGRATION_OBJECT_TYPE_LABELS[
                MigrationObjectType.ATTACHMENTS
            ],
            description: "Receipts and documents attached in QuickBooks.",
            checked: syncAttachments,
            onToggle: onToggleAttachments,
            hidden: hideAttachments,
        },
        {
            objectType: MigrationObjectType.CLASSES,
            label: MIGRATION_OBJECT_TYPE_LABELS[MigrationObjectType.CLASSES],
            description: "Class and location tags from QuickBooks.",
            checked: syncClasses,
            onToggle: onToggleClasses,
        },
        {
            objectType: MigrationObjectType.CATALOG_ITEMS,
            label: MIGRATION_OBJECT_TYPE_LABELS[
                MigrationObjectType.CATALOG_ITEMS
            ],
            description: "Catalog items sold on invoices in QuickBooks.",
            checked: syncCatalogItems,
            onToggle: onToggleCatalogItems,
        },
        {
            objectType: MigrationObjectType.INVOICES,
            label: MIGRATION_OBJECT_TYPE_LABELS[MigrationObjectType.INVOICES],
            description: "Open receivables, so AR aging matches QuickBooks.",
            checked: syncInvoices,
            onToggle: onToggleInvoices,
        },
        {
            objectType: MigrationObjectType.BILLS,
            label: MIGRATION_OBJECT_TYPE_LABELS[MigrationObjectType.BILLS],
            description: "Open payables, so AP aging matches QuickBooks.",
            checked: syncBills,
            onToggle: onToggleBills,
        },
    ];

    const cascadeNoteParams: CascadeNoteParams = {
        hideCounterparties,
        cascadeIncludesGeneralLedger,
        cascadeIncludesClasses,
    };
    const invoicesCascadeNote = getInvoicesCascadeNote(cascadeNoteParams);
    const billsCascadeNote = getBillsCascadeNote(cascadeNoteParams);

    return (
        <VStack
            gap={8}
            paddingBlock={16}
            paddingInline={24}
            className={styles.migrationCheckboxSection}
        >
            <div className={styles.migrationSectionLabel}>
                What would you like to migrate?
            </div>
            <VStack className={styles.migrationCheckboxList}>
                {rows
                    .filter((row) => !row.hidden)
                    .map((row) => (
                        <MigrationCheckboxItem
                            key={row.objectType}
                            label={row.label}
                            description={row.description}
                            checked={row.checked}
                            disabled={
                                lockedObjectTypes?.has(row.objectType) ?? false
                            }
                            lockReason={lockReasonByObjectType?.get(
                                row.objectType,
                            )}
                            onToggle={row.onToggle}
                        />
                    ))}
            </VStack>
            {(syncInvoices || (syncBills && billsCascadeNote)) && (
                <VStack gap={8} paddingInline={2}>
                    {syncInvoices && (
                        <div className={styles.migrationNote}>
                            {invoicesCascadeNote}
                        </div>
                    )}
                    {syncBills && billsCascadeNote && (
                        <div className={styles.migrationNote}>
                            {billsCascadeNote}
                        </div>
                    )}
                </VStack>
            )}
        </VStack>
    );
};
